/* ═══════════════════════════════════════════════════════════════════════════
   imageCdn — one generic "right-size this image URL" helper.

   The Lighthouse audit's biggest byte-costs were images fetched at their
   source size and displayed small: a 1998×787 / 457 KiB PNG logo shown at
   ~320 px, 1024×768 hotel photos shown in ~460 px cards. Every one of those
   hosts supports URL-level transforms, so the fix is a rewrite — the pixels
   the reader sees are identical, only the file gets smaller.

   optimizeImageUrl(url, width) rewrites URLs from hosts we KNOW:

   - Cloudinary  …/upload/…            → insert  w_<w>,c_limit,q_auto,f_auto/
                                          (f_auto = WebP/AVIF for free; c_limit
                                          never upscales). Skipped when the URL
                                          already carries a transform segment.
   - RateHawk    cdn.worldota.net/t/<size>/ → swap to the smallest catalogue
                                          template that still covers <w>.
                                          Templates are FIXED names — inventing
                                          one 404s, so we only swap DOWN
                                          between known values.
   - Unsplash    images.unsplash.com    → force  w=<w>&q=70&auto=format
                                          (auto=format = WebP/AVIF).

   Anything else (admin-uploaded hosts we don't control, data:, relative
   paths) is returned byte-identical — this helper must never be able to
   break an image, only shrink one.

   `width` is the largest CSS width the slot renders at; callers should pass
   ~2× that for retina. When in doubt, round up — c_limit/q=70 still saves
   the bulk of the bytes.
   ═══════════════════════════════════════════════════════════════════════════ */

// RateHawk's catalogue template names (width-sorted). Only these exist.
// `x500` is recognised but never picked as a target: measured 2026-09-14, most
// cdn.worldota.net images hang or error at /t/x500/ while 640x400 answers in
// ~50ms — every card that asked for ≤500px drew a blank photo.
const WORLDOTA_TEMPLATES = [
    { name: '240x240', w: 240 },
    { name: 'x500', w: 500, noTarget: true },
    { name: '640x400', w: 640 },
    { name: '1024x768', w: 1024 },
];

export function optimizeImageUrl(url, width) {
    if (typeof url !== 'string' || !url || !Number.isFinite(width) || width <= 0) return url;

    try {
        // ── Cloudinary ──────────────────────────────────────────────────
        if (url.includes('res.cloudinary.com/') && url.includes('/upload/')) {
            const [head, tail] = url.split('/upload/');
            if (!tail) return url;
            // Already transformed (w_…, q_auto, f_auto as the first segment)?
            // Leave it alone — double transforms stack and can conflict.
            if (/^(?:[a-z]+_[^/]*,?)+\//.test(tail) && /(^|,)(w_\d+|q_auto|f_auto)/.test(tail.split('/')[0])) {
                return url;
            }
            const w = Math.min(Math.round(width), 1600);
            return `${head}/upload/w_${w},c_limit,q_auto,f_auto/${tail}`;
        }

        // ── RateHawk / worldota ─────────────────────────────────────────
        const wm = url.match(/^(https?:\/\/cdn\.worldota\.net\/t\/)([^/]+)(\/.+)$/);
        if (wm) {
            const current = WORLDOTA_TEMPLATES.find((t) => t.name === wm[2]);
            // Unknown current template (e.g. '{size}' placeholder) — don't guess.
            if (!current) return url;
            const target = WORLDOTA_TEMPLATES.find((t) => t.w >= width && !t.noTarget) ||
                WORLDOTA_TEMPLATES[WORLDOTA_TEMPLATES.length - 1];
            // Only ever swap DOWN — asking for a bigger template than the
            // caller already chose would waste bytes, not save them.
            if (target.w >= current.w) return url;
            return wm[1] + target.name + wm[3];
        }

        // ── Unsplash ────────────────────────────────────────────────────
        if (url.includes('images.unsplash.com/')) {
            const u = new URL(url);
            const cur = Number(u.searchParams.get('w'));
            const w = Math.min(Math.round(width), Number.isFinite(cur) && cur > 0 ? cur : 1600);
            u.searchParams.set('w', String(w));
            u.searchParams.set('q', '70');
            u.searchParams.set('auto', 'format');
            if (!u.searchParams.get('fit')) u.searchParams.set('fit', 'crop');
            return u.toString();
        }
    } catch {
        /* malformed URL — serve it untouched rather than break the image */
    }
    return url;
}
